import React from "react";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { alpha, Box, Button, Grid, Stack, TextField, Typography, InputAdornment, Skeleton } from "@mui/material";
import Panel from "components/elements/Panel";
import ResponsiveLabel from "components/elements/ResponsiveLabel";
import { fontWeight } from "config/Theme/options";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useNewAffiliatePageData from "./module/useNewAffiliatePageData";

const NewAffiliatePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colors } = useMUIThemeModeContext();
  const { formik, isLoading, isProcessing, handleSubmit } = useNewAffiliatePageData();

  const getSkeleton = () => {
    return (
      <Grid my={6} container rowSpacing={2} columnSpacing={3}>
        <Grid item xs={12} md={6}>
          <Skeleton variant="rectangular" height={56} width={"100%"} sx={{ borderRadius: "8px" }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Skeleton variant="rectangular" height={56} width={"100%"} sx={{ borderRadius: "8px" }} />
        </Grid>
        <Grid item xs={12}>
          <Skeleton variant="rectangular" height={56} width={"100%"} sx={{ borderRadius: "8px" }} />
        </Grid>
        <Grid item xs={12}>
          <Skeleton variant="rectangular" height={56} width={"100%"} sx={{ borderRadius: "8px" }} />
        </Grid>
      </Grid>
    );
  };

  const handleChangePath = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    formik.setFieldValue("path", value.toLocaleLowerCase());
  };

  return (
    <Panel p={3}>
      <Box sx={{ position: "absolute", top: 24, left: 24 }}>
        <Button variant="text" startIcon={<ChevronLeftIcon />} onClick={() => navigate("/stosaky#affiliate")}>
          {t("affiliate.ownPage.back")}
        </Button>
      </Box>
      <Typography
        fontSize={"1.5rem"}
        fontWeight={fontWeight.semiBold}
        color={"tertiary.main"}
        textAlign={"center"}
        pt={{ xs: 4, sm: 0 }}>
        {t("affiliate.ownPage.title")}
      </Typography>
      {isLoading ? getSkeleton() : null}
      {!isLoading && (
        <>
          <Grid my={6} container rowSpacing={2} columnSpacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                autoComplete="off"
                id="nickname"
                name="nickname"
                label={ResponsiveLabel(t("affiliate.ownPage.form.nickname"))}
                value={formik.values.nickname}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.nickname && Boolean(formik.errors.nickname)}
                helperText={formik.touched.nickname && formik.errors.nickname}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                autoComplete="off"
                id="path"
                name="path"
                label={ResponsiveLabel(t("affiliate.ownPage.form.path"))}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body2" color="textSecondary" sx={{ pointerEvents: "none" }} fontSize={{ xs: "11px", sm: "16px" }}>
                        {t("affiliate.ownPage.form.pathPlaceholder")}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                value={formik.values.path}
                onChange={handleChangePath}
                onBlur={formik.handleBlur}
                error={formik.touched.path && Boolean(formik.errors.path)}
                helperText={formik.touched.path && formik.errors.path}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                autoComplete="off"
                id="title"
                name="title"
                label={ResponsiveLabel(t("affiliate.ownPage.form.title"))}
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                autoComplete="off"
                id="description"
                name="description"
                label={ResponsiveLabel(t("affiliate.ownPage.form.description"))}
                multiline
                rows={4}
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                autoComplete="off"
                id="imageDownloadUrl"
                name="imageDownloadUrl"
                label={ResponsiveLabel(t("affiliate.ownPage.form.imageDownloadUrl"))}
                value={formik.values.imageDownloadUrl}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.imageDownloadUrl && Boolean(formik.errors.imageDownloadUrl)}
                helperText={
                  (formik.touched.imageDownloadUrl && formik.errors.imageDownloadUrl)
                  || t("affiliate.ownPage.form.imageDownloadUrlHelperText")
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                autoComplete="off"
                id="youtubeId"
                name="youtubeId"
                label={ResponsiveLabel(t("affiliate.ownPage.form.youtubeId"))}
                placeholder={t("affiliate.ownPage.form.youtubeIdPlaceholder")}
                InputLabelProps={{
                  shrink: true,
                }}
                value={formik.values.youtubeId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.youtubeId && Boolean(formik.errors.youtubeId)}
                helperText={
                  (formik.touched.youtubeId && formik.errors.youtubeId)
                  || t("affiliate.ownPage.form.youtubeIdHelperText")
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                autoComplete="off"
                id="commentary"
                name="commentary"
                label={ResponsiveLabel(t("affiliate.ownPage.form.commentary"))}
                multiline
                rows={4}
                value={formik.values.commentary}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.commentary && Boolean(formik.errors.commentary)}
                helperText={formik.touched.commentary && formik.errors.commentary}
              />
            </Grid>
          </Grid>
          <Stack spacing={2} direction={"row"} justifyContent={"flex-end"}>
            <Button
              sx={{ borderColor: alpha(colors["black"], 0.1) }}
              variant="outlined"
              color="inherit"
              disabled={isLoading}
              onClick={handleSubmit("draft")}>
              {t("affiliate.ownPage.saveAsDraft")}
            </Button>
            <Button variant="contained" color="primary" onClick={handleSubmit("redirect")} disabled={isProcessing}>
              {t("affiliate.ownPage.nextStep")}
            </Button>
          </Stack>
        </>
      )}
    </Panel>
  );
};

export default NewAffiliatePage;
